import { userTypes } from "@megaron/access-control";
import { HttpAction } from "@megaron/http-service";
import { Serializers, SerializerValue } from "@megaron/serializers";
import { decodeBase64 } from "@megaron/utils";
import { newUuid, uuidSerializer } from "@megaron/uuid";

export const idTokenPayloadSerializer = Serializers.object({
  id: Serializers.string,
  userType: Serializers.stringOneOf(...userTypes),
  loyaltyId: uuidSerializer.nullable(),
  firstName: Serializers.string.nullable(),
  lastName: Serializers.string.nullable(),
  profilePictureUrl: Serializers.string.nullable(),
  email: Serializers.string.nullable(),
  phoneNumber: Serializers.string.nullable(),

  groups: Serializers.array(Serializers.object({ id: Serializers.string, name: Serializers.string })),
  roles: Serializers.string.array(),
  attributes: Serializers.string.array(),
  exp: Serializers.integer,
  iat: Serializers.integer,
  refreshTokenId: uuidSerializer.nullable().optional(),
});

export type IdTokenPayload = SerializerValue<typeof idTokenPayloadSerializer>;

export const myAccountDtoSerializer = Serializers.object({
  uuid: uuidSerializer,
  phoneNumber: Serializers.string,
  scheduledDeletionTime: Serializers.datetime.nullable(),
  firstName: Serializers.string.nullable(),
  lastName: Serializers.string.nullable(),
  email: Serializers.string.nullable(),
  unverifiedEmail: Serializers.string.nullable(),
  isRegistered: Serializers.boolean,
  isVerified: Serializers.boolean,
  emailConsent: Serializers.boolean,
  phoneCallConsent: Serializers.boolean,
  smsConsent: Serializers.boolean,
  postalCode: Serializers.postalCode.nullable(),
  loyaltyMemberSince: Serializers.datetime.nullable(),
});

export type MyAccountDto = SerializerValue<typeof myAccountDtoSerializer>;

export const accountDtoSerializer = Serializers.object({
  uuid: uuidSerializer,
  phoneNumber: Serializers.string,
  email: Serializers.email.nullable(),
  firstName: Serializers.string.nullable(),
  lastName: Serializers.string.nullable(),
  unverifiedEmail: Serializers.string.nullable(),
  deletedAt: Serializers.datetime.nullable(),
  scheduledDeletionTime: Serializers.datetime.nullable(),
  joinedAt: Serializers.datetime.nullable(),
  registeredAt: Serializers.datetime.nullable(),
  postalCode: Serializers.postalCode.nullable(),
  loyaltyMemberSince: Serializers.datetime.nullable(),
});

export type AccountDto = SerializerValue<typeof accountDtoSerializer>;

export const newAccountDto = (acc: Partial<AccountDto>): AccountDto => ({
  uuid: newUuid(),
  deletedAt: null,
  email: null,
  firstName: "",
  joinedAt: new Date(),
  lastName: "",
  phoneNumber: "+48123123123",
  registeredAt: new Date(),
  scheduledDeletionTime: null,
  unverifiedEmail: null,
  postalCode: null,
  loyaltyMemberSince: null,
  ...acc,
});

export const testUserSignInOptionsSerializer = Serializers.object({
  userId: Serializers.string,
  userType: Serializers.stringOneOf(...userTypes),
  phoneNumber: Serializers.string,
}).partial();

export type TestUserSignInOptions = SerializerValue<typeof testUserSignInOptionsSerializer>;

const signInErrorSerializer = Serializers.stringOneOf(
  "InvalidToken",
  "NoCredentialsProvided",
  "InvalidRefreshToken",
  "AccountInactive",
  "AccountNotFound",
  "InvalidApiKey",
  "InvalidOtp",
  "RequestExpired",
  "TooManyAttempts",
  "NotAllowedToIssueRefreshToken",
  "InvalidTransferToken",
);

export type SignInErorr = SerializerValue<typeof signInErrorSerializer>;

const otpSignInOptionsSerializer = Serializers.object({
  uuid: uuidSerializer,
  password: Serializers.string,
  ...Serializers.object({
    joinLoyalty: Serializers.boolean.optional(),
  }).partial().fields,
});

export type OtpSignInOptions = SerializerValue<typeof otpSignInOptionsSerializer>;

const demoUserSignInOptionsSerializer = Serializers.object({
  phoneNumber: Serializers.string,
  password: Serializers.string,
  ...Serializers.object({
    joinLoyalty: Serializers.boolean.optional(),
  }).partial().fields,
});

export type DemoUserSignInOptions = SerializerValue<typeof demoUserSignInOptionsSerializer>;

const signInResponseSerializer = Serializers.object({
  idTokenPayload: idTokenPayloadSerializer,
  idTokenExpiresAt: Serializers.datetime,
  idToken: Serializers.string,
  refreshToken: Serializers.object({
    id: uuidSerializer,
    expiresAt: Serializers.datetime,
    legacyTokenValue: Serializers.string.optional(),
  }).nullable(),
});

export type SignInResponse = SerializerValue<typeof signInResponseSerializer>;

export const authActions = {
  signIn: HttpAction({
    path: "/signIn",
    method: "post",
    bodySerializer: Serializers.object({
      googleIdToken: Serializers.string,
      apiKey: Serializers.string,
      otp: otpSignInOptionsSerializer,
      testUser: testUserSignInOptionsSerializer.partial(),
      demoUser: demoUserSignInOptionsSerializer,
      transferToken: Serializers.string,
      legacyRefreshToken: Serializers.string,
      issueRefreshToken: Serializers.stringOneOf("cookie", "legacy"),
      refreshTokenCookiePath: Serializers.string,
      setIdTokenCookie: Serializers.boolean,
    }).partial(),
    valueSerializer: signInResponseSerializer,
    errorSerializer: signInErrorSerializer,
  }),

  requestOtp: HttpAction({
    path: "/requestOtp",
    method: "post",
    bodySerializer: Serializers.first(
      Serializers.object({ phoneNumber: Serializers.string, recaptchaToken: Serializers.string }),
      Serializers.object({ accountUuid: uuidSerializer, recaptchaToken: Serializers.string }),
    ),
    valueSerializer: Serializers.object({ uuid: uuidSerializer }),
    errorSerializer: Serializers.stringOneOf("InvalidPhoneNumber", "FailedRecaptcha", "AccountNotFound"),
  }),

  signOut: HttpAction({
    path: "/signOut",
    method: "post",
    requiresAuth: true as const,
    bodySerializer: Serializers.object({
      refreshTokenId: Serializers.string,
      allTokens: Serializers.boolean,
      refreshTokenCookiePath: Serializers.string.optional(),
      clearIdTokenCookie: Serializers.boolean.optional(),
    }).partial(),
    errorSerializer: Serializers.stringOneOf("TokenNotFound"),
  }),

  myAccount: HttpAction({
    path: "/myAccount",
    method: "get",
    valueSerializer: myAccountDtoSerializer,
    errorSerializer: Serializers.stringOneOf("AccountNotFound", "AccountDeleted"),
    requiresAuth: true as const,
  }),

  joinLoyalty: HttpAction({
    path: "/joinLoyalty",
    method: "post",
    requiresAuth: true as const,
    errorSerializer: Serializers.stringOneOf("AccountNotFound", "AccountDeleted", "AlreadyJoined"),
  }),

  account: HttpAction({
    path: "/account/uuid/:uuid",
    method: "get",
    requiresAuth: true as const,
    valueSerializer: accountDtoSerializer,
    errorSerializer: Serializers.stringOneOf("AccountNotFound"),
    paramsSerializer: Serializers.object({ uuid: uuidSerializer }),
  }),

  publicProfileQuery: HttpAction({
    method: "get",
    path: "/publicProfiles/uuid/:uuid",
    paramsSerializer: Serializers.object({
      uuid: uuidSerializer,
    }),
    valueSerializer: Serializers.object({
      uuid: uuidSerializer,
      firstName: Serializers.string.nullable(),
      lastName: Serializers.string.nullable(),
    }),
    errorSerializer: Serializers.stringOneOf("AccountNotFound"),
  }),

  requestAccountDeletion: HttpAction({
    path: "/requestAccountDeletion",
    method: "post",
    errorSerializer: Serializers.stringOneOf("DeletionAlreadyScheduled", "AccountNotFound", "AccountDeleted"),
    requiresAuth: true as const,
  }),

  cancelAccountDeletion: HttpAction({
    path: "/cancelAccountDeletion",
    method: "post",
    errorSerializer: Serializers.stringOneOf("DeletionNotScheduled", "AccountNotFound", "AccountDeleted"),
    requiresAuth: true as const,
  }),

  executeScheduledDeletions: HttpAction({
    path: "/executeScheduledDeletions",
    method: "post",
    requiresAuth: true as const,
    bodySerializer: Serializers.object({ cutoff: Serializers.datetime.optional() }),
  }),

  generateTransferToken: HttpAction({
    path: "/generateTransferToken",
    requiresAuth: true as const,
    method: "post",
    errorSerializer: Serializers.stringOneOf("NoRefreshToken"),
    valueSerializer: Serializers.object({ transferToken: Serializers.string }),
  }),
};

export const decodeIdToken = (idToken: string): IdTokenPayload => {
  const payloadB64 = idToken.split(".")[1];
  const payloadJsonStr = decodeBase64(payloadB64);
  const payloadJson = JSON.parse(payloadJsonStr);

  return idTokenPayloadSerializer.deserialize(payloadJson).assertOk().value;
};
